import { SubAccountStates } from '@generated/graphql';
import type { PortfolioReport } from '@interfaces/portfolioReport';
import type { SubAccountReport } from '@interfaces/subAccountReport';
import type { CurrencyCodes } from '@onevesthq/ov-enums';
import type { TFunction } from 'i18next';
import { formatAuditAccountType } from '../presentation/accountTypeLabels';
import {
  resolvePortfolioBaseCurrency,
  resolvePortfolioDisplayCurrencies,
} from '../presentation/portfolioCurrencyPresentation';
import { resolvePortfolioInvestableCentsFromSubAccounts } from './resolvePortfolioInvestableCents';

export type ReportHoldingDisplayMetadata = {
  currentPriceCents: number;
  financialProductTaxRank: number;
  taxRank: number | null;
  settlementDays?: number;
  translatedName: string;
  currency?: CurrencyCodes;
  originalCurrency?: CurrencyCodes;
};

export type ReportSubAccountHoldingMetadata = {
  valueCents: number;
  expectedPercentage: number;
  expectedValueCents: number;
  quantity: number;
};

export type ReportHoldingValueMetadata = {
  valueCents: number;
  expectedValueCents: number;
  expectedPercentage: number;
  quantity: number;
  bySubAccount: Map<string, ReportSubAccountHoldingMetadata>;
};

export type ReportSubAccountDisplayMetadata = {
  id: string;
  name: string;
  accountType: string;
  inactive: boolean;
  taxRank: number;
  marketValueCents?: number;
  cashCents: number;
  expectedCashCents: number;
  cashOnHoldToTradeCents: number;
  pendingWithdrawCents: number;
  pendingContributionCents: number;
  currency: CurrencyCodes;
  accountBaseCurrency: CurrencyCodes;
  originalValues?: SubAccountReport['originalValues'];
};

export type ReportPortfolioCashMetadata = {
  cashCents: number;
  expectedCashCents: number;
  expectedCashPercentage: number;
  transferRebalanceRequired: boolean;
  liquidateRebalanceRequired: boolean;
};

export type ReportDisplayMetadata = {
  holdingsByProductId: Map<string, ReportHoldingDisplayMetadata>;
  holdingsValuesByProductId: Map<string, ReportHoldingValueMetadata>;
  subAccountsById: Map<string, ReportSubAccountDisplayMetadata>;
  portfolioCash?: ReportPortfolioCashMetadata;
  baseCurrency: CurrencyCodes;
  displayCurrencies: CurrencyCodes[];
  totalMarketValueCents: number;
  totalInvestableCents: number;
};

/** Display and holdings metadata from a portfolio report for audit grid rendering. */
export const buildReportDisplayMetadata = (
  translateBackendString: (translatedObject: unknown) => string,
  report: PortfolioReport | undefined,
  t: TFunction,
): ReportDisplayMetadata => {
  const holdingsByProductId = new Map<string, ReportHoldingDisplayMetadata>();
  const holdingsValuesByProductId = new Map<
    string,
    ReportHoldingValueMetadata
  >();
  const subAccountsById = new Map<string, ReportSubAccountDisplayMetadata>();
  const baseCurrency = resolvePortfolioBaseCurrency(report);

  for (const holding of report?.holdings ?? []) {
    const productId = holding.financialProduct?.id;
    if (!productId) {
      continue;
    }

    if (!holdingsByProductId.has(productId)) {
      holdingsByProductId.set(productId, {
        currentPriceCents: holding.financialProduct?.currentPriceCents ?? 0,
        financialProductTaxRank: holding.financialProduct?.taxRank ?? 0,
        taxRank: holding.taxRank || null,
        settlementDays: holding.financialProduct?.settlementDays ?? undefined,
        translatedName:
          translateBackendString(holding.financialProduct?.translatedName) ||
          holding.financialProduct?.ticker ||
          '',
        currency: holding.currency ?? baseCurrency,
        originalCurrency:
          holding.financialProduct?.originalCurrency ??
          holding.currency ??
          baseCurrency,
      });
    }

    const existingValues = holdingsValuesByProductId.get(productId) ?? {
      valueCents: 0,
      expectedValueCents: holding.expectedValueCents ?? 0,
      expectedPercentage: holding.expectedPercentage ?? 0,
      quantity: holding.quantity ?? 0,
      bySubAccount: new Map<string, ReportSubAccountHoldingMetadata>(),
    };

    if ((holding.quantity ?? 0) > 0) {
      existingValues.quantity = holding.quantity ?? 0;
    }

    if ((holding.expectedValueCents ?? 0) > 0) {
      existingValues.expectedValueCents = holding.expectedValueCents ?? 0;
    }
    if ((holding.expectedPercentage ?? 0) > 0) {
      existingValues.expectedPercentage = holding.expectedPercentage ?? 0;
    }

    holdingsValuesByProductId.set(productId, existingValues);
  }

  for (const subAccountReport of report?.subAccounts ?? []) {
    const subAccountId = subAccountReport.subAccount?.id;
    const accountType = formatAuditAccountType(
      subAccountReport.subAccount?.account?.type,
    );
    if (!subAccountId || !accountType || accountType === '—') {
      continue;
    }

    const { subAccount } = subAccountReport;
    const userName =
      subAccount.account.user.firstName ?? subAccount.account.user.entityName;
    const name = `${userName}'s ${t(`accountTypes:${subAccount.account.type}`)}`;

    subAccountsById.set(subAccountId, {
      id: subAccountId,
      name,
      accountType,
      inactive: subAccount.state === SubAccountStates.Inactive,
      taxRank: subAccount.account.taxRank ?? 0,
      marketValueCents: subAccountReport.marketValueCents,
      cashCents: subAccountReport.cashCents ?? 0,
      expectedCashCents: subAccountReport.expectedCashCents ?? 0,
      cashOnHoldToTradeCents: subAccountReport.cashOnHoldToTradeCents ?? 0,
      pendingWithdrawCents: subAccountReport.pendingWithdrawCents ?? 0,
      pendingContributionCents: subAccountReport.pendingContributionCents ?? 0,
      currency: subAccountReport.currency ?? baseCurrency,
      accountBaseCurrency:
        subAccountReport.accountBaseCurrency ??
        subAccountReport.currency ??
        baseCurrency,
      originalValues: subAccountReport.originalValues,
    });

    for (const holding of subAccountReport.holdings ?? []) {
      const productId = holding.financialProduct?.id;
      if (!productId) {
        continue;
      }

      const valueCents = holding.valueCents ?? 0;
      const existingValues = holdingsValuesByProductId.get(productId) ?? {
        valueCents: 0,
        expectedValueCents: holding.expectedValueCents ?? 0,
        expectedPercentage: holding.expectedPercentage ?? 0,
        quantity: 0,
        bySubAccount: new Map<string, ReportSubAccountHoldingMetadata>(),
      };

      existingValues.bySubAccount.set(subAccountId, {
        valueCents,
        expectedPercentage: holding.expectedPercentage ?? 0,
        expectedValueCents: holding.expectedValueCents ?? 0,
        quantity: holding.quantity ?? 0,
      });

      holdingsValuesByProductId.set(productId, existingValues);
    }
  }

  const subAccountReports = report?.subAccounts ?? [];
  const totalMarketValueCents = subAccountReports.reduce(
    (sum, subAccountReport) => sum + (subAccountReport.marketValueCents ?? 0),
    0,
  );

  for (const [productId, values] of holdingsValuesByProductId.entries()) {
    if (values.bySubAccount.size > 0) {
      const summedExpectedValueCents = [...values.bySubAccount.values()].reduce(
        (sum, entry) => sum + entry.expectedValueCents,
        0,
      );

      values.valueCents = [...values.bySubAccount.values()].reduce(
        (sum, entry) => sum + entry.valueCents,
        0,
      );
      values.quantity = [...values.bySubAccount.values()].reduce(
        (sum, entry) => sum + entry.quantity,
        0,
      );

      // Portfolio-level target % is authoritative when present. When it is
      // missing on multi-sleeve reports, top-level $ can be on a different
      // basis — sum sleeve targets instead (matches the live portfolio widget).
      if (values.expectedPercentage <= 0 && summedExpectedValueCents > 0) {
        values.expectedValueCents = summedExpectedValueCents;
      } else if (
        values.expectedValueCents === 0 &&
        summedExpectedValueCents > 0
      ) {
        values.expectedValueCents = summedExpectedValueCents;
      }

      if (
        values.expectedPercentage <= 0 &&
        values.expectedValueCents > 0 &&
        totalMarketValueCents > 0
      ) {
        values.expectedPercentage =
          values.expectedValueCents / totalMarketValueCents;
      }

      holdingsValuesByProductId.set(productId, values);
    } else if (values.valueCents === 0) {
      const topLevelHolding = report?.holdings?.find(
        (holding) => holding.financialProduct?.id === productId,
      );
      values.valueCents = topLevelHolding?.valueCents ?? 0;
      values.quantity = topLevelHolding?.quantity ?? values.quantity;

      if (
        values.expectedPercentage <= 0 &&
        values.expectedValueCents > 0 &&
        totalMarketValueCents > 0
      ) {
        values.expectedPercentage =
          values.expectedValueCents / totalMarketValueCents;
      }

      holdingsValuesByProductId.set(productId, values);
    }
  }

  const totalInvestableCents = resolvePortfolioInvestableCentsFromSubAccounts(
    subAccountReports.map((subAccountReport) => ({
      marketValueCents: subAccountReport.marketValueCents,
      expectedCashCents: subAccountReport.expectedCashCents,
    })),
  );

  const portfolioCash = report
    ? {
        cashCents: report.cashCents ?? 0,
        expectedCashCents: report.expectedCashCents ?? 0,
        expectedCashPercentage: report.expectedCashPercentage ?? 0,
        transferRebalanceRequired: report.transferRebalanceRequired ?? false,
        liquidateRebalanceRequired: report.liquidateRebalanceRequired ?? false,
      }
    : undefined;

  return {
    holdingsByProductId,
    holdingsValuesByProductId,
    subAccountsById,
    portfolioCash,
    baseCurrency,
    displayCurrencies: resolvePortfolioDisplayCurrencies(report),
    totalMarketValueCents,
    totalInvestableCents,
  };
};
